import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #busyTimeout;

  /** @type {MutationObserver | null} */
  #bundleObserver = null;

  /** @type {number} */
  #bundleFrame = 0;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    this.#setupBundleMirror();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#bundleObserver?.disconnect();
    cancelAnimationFrame(this.#bundleFrame);
    clearTimeout(this.#busyTimeout);
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = () => {
    const { addToCartButton } = this.refs;
    if (!this.#targetAddToCartButton || addToCartButton.getAttribute('aria-busy') === 'true') return;

    // Winterborn: no fly-to-cart animation, the sticky button shows a loader until the add settles
    addToCartButton.setAttribute('aria-busy', 'true');
    clearTimeout(this.#busyTimeout);
    this.#busyTimeout = window.setTimeout(this.#stopBusy, 8000);

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
  };

  #stopBusy = () => {
    clearTimeout(this.#busyTimeout);
    this.refs.addToCartButton?.removeAttribute('aria-busy');
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.#syncBundle();
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
      this.#stopBusy();
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Bundle mirror
  /**
   * Mirrors the Vitals "Double pack / Buy 3" tier chosen on the page into the sticky bar
   * (name, picked colours, photos, price and the struck-through price).
   * Vitals renders the tiers at runtime, so we watch the section and re-read the selected tier.
   */
  #setupBundleMirror() {
    const root = this.closest('.shopify-section');
    if (!root) return;

    this.#bundleObserver = new MutationObserver((records) => {
      // Ignore our own writes into the bar
      if (records.every((record) => this.contains(record.target))) return;
      cancelAnimationFrame(this.#bundleFrame);
      this.#bundleFrame = requestAnimationFrame(this.#syncBundle);
    });
    this.#bundleObserver.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'src', 'aria-checked'],
    });

    this.#syncBundle();
  }

  /**
   * Reads the selected Vitals tier. Returns null for a single item (no struck-through price).
   * @returns {{ title: string, price: string, compare: string, picks: string[], images: string[] } | null}
   */
  #readBundleTier() {
    const tier = document.querySelector(
      '.Vtl-NewVolumeDiscountRadio__TierItem:not(.Vtl-NewVolumeDiscountRadio__TierItem--NotSelected)'
    );
    if (!tier) return null;

    /** @param {Element | null | undefined} el */
    const text = (el) => (el?.textContent ?? '').replace(/\s+/g, ' ').trim();

    // Vitals adds screen-reader words ("Sale price", "Regular price") inside the price elements: keep only the amount
    /** @param {Element | null | undefined} el */
    const amount = (el) => text(el).match(/[$€£¥]?\s?\d[\d.,]*\s?[€£¥]?/)?.[0].trim() ?? '';

    const title = text(tier.querySelector("[data-vtl-el='title']"));
    const price = amount(tier.querySelector("[data-vtl-el='price']"));
    const compare = amount(tier.querySelector("[data-vtl-el='compare-price']") ?? tier.querySelector('s, del'));
    if (!title || !price || !compare) return null;

    const picks = [...tier.querySelectorAll('.Vtl-Dropdown__Header')].map(text).filter(Boolean);
    const images = [...tier.querySelectorAll("img[class*='TierImage'], [class*='TierImage'] img")]
      .map((img) => (img instanceof HTMLImageElement ? img.currentSrc || img.src : ''))
      .filter(Boolean);

    return { title, price, compare, picks, images };
  }

  #syncBundle = () => {
    const { stickyBar } = this.refs;
    if (!stickyBar) return;

    const bundle = this.#readBundleTier();
    if (!bundle) {
      if (stickyBar.hasAttribute('data-bundle')) {
        stickyBar.removeAttribute('data-bundle');
        stickyBar.querySelectorAll('[data-bundle-el]').forEach((el) => el.remove());
      }
      return;
    }

    stickyBar.setAttribute('data-bundle', 'true');

    /**
     * Finds or creates a mirrored element inside `parent`.
     * @param {Element | null} parent
     * @param {string} name
     * @param {string} tag
     * @param {Element | null} [before]
     * @returns {HTMLElement | null}
     */
    const ensure = (parent, name, tag, before = null) => {
      if (!parent) return null;
      let el = /** @type {HTMLElement | null} */ (parent.querySelector(`:scope > [data-bundle-el='${name}']`));
      if (!el) {
        el = document.createElement(tag);
        el.dataset.bundleEl = name;
        el.className = `sticky-add-to-cart__bundle-${name}`;
        parent.insertBefore(el, before);
      }
      return el;
    };

    /** @param {HTMLElement | null} el @param {string} value */
    const setText = (el, value) => {
      if (el && el.textContent !== value) el.textContent = value;
    };

    // Label: "Double pack · Amethyst Sheen, Mulberry Sheen"
    const info = stickyBar.querySelector('.sticky-add-to-cart__info');
    setText(ensure(info, 'label', 'div'), [bundle.title, bundle.picks.join(', ')].filter(Boolean).join(' · '));

    // Price: current price then the struck-through original
    const priceBox = stickyBar.querySelector('.sticky-add-to-cart__price');
    const priceEl = ensure(priceBox, 'price', 'div');
    if (priceEl) {
      setText(ensure(priceEl, 'now', 'span'), bundle.price);
      const was = ensure(priceEl, 'was', 'del');
      setText(was, bundle.compare);
    }

    // Photos of the chosen items, overlapping
    const imagesEl = ensure(stickyBar, 'images', 'div', info);
    if (imagesEl) {
      const key = bundle.images.join('|');
      if (imagesEl.dataset.key !== key) {
        imagesEl.dataset.key = key;
        imagesEl.replaceChildren(
          ...bundle.images.slice(0, 3).map((src) => {
            const img = document.createElement('img');
            img.src = src;
            img.alt = '';
            return img;
          })
        );
      }
    }
  };

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
